import * as React from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSpring, animated } from 'react-spring';
import { Logo } from './logo';
import { DesktopMenuElements } from './desktop-menu-elements';
import { ModalMenu } from './modal-menu';
import { getClient, getMenuElements } from './query'; // old graphql implementation, replaced by hardcoded globalMenu below
import type { IGlobalMenu, IMenuElement, IMenuProps } from './menytypes';

interface IMenuState {
  modalVisible: boolean;
  searchVisible: boolean;
}

type ContextProps = {
  menuState: IMenuState;
  setMenuState: any;
};

export const MenuContext = React.createContext<ContextProps>({
  menuState: { modalVisible: false, searchVisible: false },
  setMenuState: null
});

const globalMenu: IGlobalMenu = {
  // moved here from rt_pm_graph to avoid poor performance
  menu: [
    {
      name: 'Kjøpe eiendom',
      link: 'https://privatmegleren.no/kjope-bolig?a=false&c=null&l=%5B%5D&mb=0&mv=false&p=1&pfv=1000000&ptv=25000000&q=&s=false&sfv=30&show_market_link=false&stv=400',
      subMenu: null
    },
    {
      name: 'Fritidseiendom',
      link: 'https://privatmegleren.no/selge-fritidseiendom-hytte',
      subMenu: null
    },
    {
      name: 'Nybygg',
      link: 'https://privatmegleren.no/nybygg',
      subMenu: null
    },
    {
      name: 'Selge eiendom / Verdivurdering',
      link: 'https://privatmegleren.no/verdivurdering-selge-eiendom',
      subMenu: null
    },
    {
      name: 'Finansiering',
      link: 'https://privatmegleren.no/finansiering-boliglan',
      subMenu: null
    },
    {
      name: 'Kontor / Megler',
      link: 'https://privatmegleren.no/kontorer?alle',
      subMenu: null
    }
  ],
  nybyggmenu: [
    {
      name: 'Til salgs',
      link: 'https://privatmegleren.no/kjope-bolig?sd=NYBYGG',
      subMenu: null
    },
    {
      name: 'Finn megler',
      link: 'https://privatmegleren.no/kontorer?nybygg',
      subMenu: null
    }
  ],
  submenu: [
    {
      name: 'Kjøpe',
      link: 'https://privatmegleren.no/kjope-bolig?a=false&c=null&l=%5B%5D&mb=0&mv=false&p=1&pfv=1000000&ptv=25000000&q=&s=false&sfv=30&show_market_link=false&stv=400',
      subMenu: [
        {
          name: 'Kjøpe eiendom',
          link: 'https://privatmegleren.no/kjope-bolig?a=false&c=null&l=%5B%5D&mb=0&mv=false&p=1&pfv=1000000&ptv=25000000&q=&s=false&sfv=30&show_market_link=false&stv=400'
        },
        {
          name: 'Fritidseiendom',
          link: 'https://privatmegleren.no/selge-fritidseiendom-hytte'
        },
        {
          name: 'Finansiering',
          link: 'https://privatmegleren.no/finansiering-boliglan'
        },
        {
          name: 'Hjelp til kjøp',
          link: 'https://privatmegleren.no/kjope-eiendom'
        },
        {
          name: 'Boligbytte - Hjelp med flytting',
          link: 'https://privatmegleren.no/boligbytte-selge-eiendom'
        },
        {
          name: 'Registrer ditt eiendomssøk',
          link: 'https://privatmegleren.no/boligsok'
        }
      ]
    },
    {
      name: 'Selge',
      link: '/verdivurdering-selge-eiendom',
      subMenu: [
        {
          name: 'Bruktbolig',
          link: 'https://privatmegleren.no/kontorer?alle'
        },
        {
          name: 'Nybygg',
          link: 'https://privatmegleren.no/kontorer?nybygg-salg'
        },
        {
          name: 'Fritidseiendom',
          link: 'https://privatmegleren.no/selge-fritidseiendom-hytte'
        },
        {
          name: 'Bestill e-takst/verdivurdering',
          link: 'https://privatmegleren.no/verdivurdering-selge-eiendom'
        },
        {
          name: 'Boligbytte - Hjelp med flytting',
          link: 'https://privatmegleren.no/boligbytte-selge-eiendom'
        },
        {
          name: 'Vår leveranse',
          link: 'https://privatmegleren.no/leveransebeskrivelse-selge-bolig'
        }
      ]
    },
    {
      name: 'Nybygg',
      link: '/nybygg',
      subMenu: [
        {
          name: 'Om nybygg',
          link: 'https://privatmegleren.no/nybygg'
        },
        {
          name: 'Nybygg til salgs',
          link: 'https://privatmegleren.no/kjope-bolig?sd=NYBYGG'
        },
        {
          name: 'Finn megler',
          link: 'https://privatmegleren.no/kontorer?nybygg-salg'
        },
        {
          name: 'Finansiering',
          link: 'https://privatmegleren.no/finansiering-nybygg'
        },
        {
          name: 'Hjelp til kjøp',
          link: 'https://privatmegleren.no/kjope-eiendom'
        },
        {
          name: 'Registrer ditt eiendomssøk',
          link: 'https://privatmegleren.no/boligsok'
        }
      ]
    },
    {
      name: 'Næring',
      link: 'https://privatmegleren.no/naering'
    },
    {
      name: 'Fritidseiendom',
      link: 'https://privatmegleren.no/selge-fritidseiendom-hytte'
    },
    {
      name: 'Finansiering',
      link: 'https://privatmegleren.no/finansiering-boliglan',
      subMenu: [
        {
          name: 'Bruktbolig, fritid og næringseiendom',
          link: 'https://privatmegleren.no/finansiering-boliglan'
        },
        {
          name: 'Nybygg',
          link: 'https://privatmegleren.no/finansiering-nybygg'
        }
      ]
    },
    {
      name: 'Kontor og megler',
      link: '/kontorer?alle',
      subMenu: [
        {
          name: 'Søk megler eller kontor',
          link: 'https://privatmegleren.no/kontorer?alle'
        },
        {
          name: 'Søk i nærheten',
          link: 'https://privatmegleren.no/kontorer/inarheten'
        }
      ]
    },
    {
      name: 'Digital boligannonsering',
      link: 'https://privatmegleren.no/kampanje/presisjon'
    },
    {
      name: 'Styling og klargjøring',
      link: 'https://privatmegleren.no/styling-og-klargjoring'
    },
    {
      name: 'Kjøpsmegling',
      link: 'https://privatmegleren.no/kjope-eiendom'
    },
    {
      name: 'Stillinger',
      link: 'https://privatmegleren.no/stillinger'
    },
    {
      name: 'Om oss',
      link: 'https://privatmegleren.no/om-oss'
    }
  ]
};
export const Menu = (props: IMenuProps) => {
  const [menuState, setMenuState] = React.useState<IMenuState>({
    modalVisible: false,
    searchVisible: false
  });

  const fadeIn = useSpring({
    position: 'fixed',
    transitionTimingFunction: 'ease-in-out',
    opacity: menuState.modalVisible ? 1 : 0,
    pointerEvents: menuState.modalVisible ? 'auto' : 'none'
  });

  // const isSmall = typeof window !== 'undefined' && window.innerWidth < 600;

  const bgfadeIn = useSpring({
    position: 'absolute',
    top: '0',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: -1,
    display:
      menuState.modalVisible || menuState.searchVisible ? 'block' : 'none',
    transitionTimingFunction: 'ease-in-out',
    opacity: menuState.modalVisible || menuState.searchVisible ? 1 : 0
  });

  const shouldShowLogo = () => {
    return (
      props?.config?.logo !== false ||
      menuState.modalVisible ||
      menuState.searchVisible
    );
  };

  let className = '';
  if (props.className) {
    className = `${props.className}`;
  }
  if (menuState.modalVisible || menuState.searchVisible) {
    className += ' menu-open';
  }
  if (!props?.config?.transparent) {
    className += ' menu-background';
  } else {
    className += ' menu-no-background';
  }

  return (
    <MenuContext.Provider value={{ menuState, setMenuState }}>
      <MenuWrapper
        className={className}
        zIndex={menuState.modalVisible || menuState.searchVisible ? 200 : 100}
        transparent={props?.config?.transparent || false}
      >
        <AnimatedDiv style={bgfadeIn}>
          <Bg />
        </AnimatedDiv>
        {shouldShowLogo() && <Logo />}
        <FlexEnd>
          <DesktopMenuElements
            vistopp={props.config?.vistopp === false ? false : true}
            menuelements={
              props.config?.nybygg ? globalMenu?.nybyggmenu : globalMenu?.menu
            }
            history={props.history}
          />
        </FlexEnd>
      </MenuWrapper>
      <ModalMenuAnimationHolder style={fadeIn}>
        <ModalMenu menuelements={globalMenu.submenu} />
      </ModalMenuAnimationHolder>
    </MenuContext.Provider>
  );
};

const FlexEnd = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const AnimatedDiv = styled(animated.div)<any>``;

const ModalMenuAnimationHolder = styled(animated.div)<any>`
  position: fixed;
  top: 56px;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 200;
  line-height: 1.3;

  @media (min-width: 1140px) {
    left: auto;
  }
`;

const MenuWrapper = styled.div<{ transparent: boolean; zIndex: number }>`
  font-size: 18px;
  position: fixed;
  line-height: 1.3;
  left: 0;
  right: 0;
  top: 0;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  height: 66px;
  background-color: ${(props) => (props.transparent ? 'none' : '#171719')};
  z-index: ${(props) => (props.zIndex ? props.zIndex : 100)};
  padding: 0 30px;
`;

const Bg = styled.div`
  background-color: rgba(0, 0, 0, 0.9);
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  position: absolute;
  z-index: -1;
`;
